<template>
  <div class="job-status-multi-ac">
    <label v-if="props.label" class="form-label">{{ props.label }}</label>
    <job-status-ac 
      :key="statusSelectorKey"
      v-model="selectedStatusForAdd" 
      :placeholder="props.placeholder"
      @change="addStatus"
    />
    <div v-if="model.length > 0" class="d-flex flex-wrap gap-2 mt-2">
      <span 
        v-for="statusId in model" 
        :key="statusId"
        class="badge bg-primary d-flex align-items-center gap-2"
        style="font-size: 0.9rem; padding: 0.4rem 0.6rem;"
      >
        {{ getStatusName(statusId) }}
        <button 
          type="button" 
          class="btn-close btn-close-white" 
          style="font-size: 0.6rem;"
          @click="removeStatus(statusId)"
          aria-label="Remove"
        ></button>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, watch } from 'vue';
import JobStatusAc from '@/components/Jobs/JobStatusAc.vue';
import { useJobStatusStore } from '@/stores/JobStatusStore';

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Select status to add...'
  }
});

const model = defineModel<number[]>({
  default: () => []
});

const jobStatusStore = useJobStatusStore();
const selectedStatusForAdd = ref('');
const statusSelectorKey = ref(0);

// Initialize job status store
onMounted(async () => {
  await jobStatusStore.load();
  console.log('JobStatusMultiAc mounted, initial model:', model.value);
});

// Watch model changes
watch(model, (newVal, oldVal) => {
  console.log('Model changed from', oldVal, 'to', newVal);
}, { deep: true });

// Watch selectedStatusForAdd and immediately process when it gets a value
watch(selectedStatusForAdd, async (newVal) => {
  console.log('selectedStatusForAdd changed to:', newVal, 'type:', typeof newVal);
  
  if (!newVal) return;
  
  // Convert to string if it's a number
  const statusIdStr = String(newVal);
  const id = parseInt(statusIdStr);
  
  console.log('Processing status ID:', id, 'Current model:', model.value);
  
  if (!isNaN(id) && !model.value.includes(id)) {
    // Create new array to ensure reactivity
    model.value = [...model.value, id];
    console.log('Added status, new model:', model.value);
  }
  
  // Immediately reset the selector by incrementing key
  // This forces JobStatusAc to recreate with empty v-model
  await nextTick();
  statusSelectorKey.value++;
  selectedStatusForAdd.value = '';
  console.log('Selector reset, new key:', statusSelectorKey.value);
});

// Add status to selection (keeping this for the @change event, but watcher does the work)
const addStatus = async (statusId: string) => {
  console.log('addStatus @change event:', statusId);
};

// Remove status from selection
const removeStatus = (statusId: number) => {
  model.value = model.value.filter(id => id !== statusId);
};

// Get status name from store
const getStatusName = (statusId: number): string => {
  const status = jobStatusStore.list[statusId];
  return status?.Status || 'Unknown';
};
</script>

<style scoped>
.job-status-multi-ac {
  width: 100%;
}
</style>
